import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { Col, Collapse, Row, Skeleton } from "antd";
import {
  getGroupedCountData,
  getSalesTrendData,
  getGroupedBySourceData,
  getGroupedByIndustryData,
  getConvertedLeadsData,
} from "../../actionCreator/dashboardActionCreator";
import { getUserInfo } from "../../utils/utils";
import { getLeadStatusList } from "../../actionCreator/leadListingActionCreater";
import ChartRenderer from "./ChartRenderer";
import { ChevronUp, ChevronDown, Filter, Send, TrendingUp } from "lucide-react";
import seprator from "../../assets/svg/icons/seprator.svg";
import { AuthContext } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertProvider";

const getSentimentColor = ({ sentiment }) => {
  if (sentiment === "High") return "#5D44FF";
  if (sentiment === "Medium") return "#8C4BAA";
  return "#559BA3";
};

const MarketingReport = ({ userId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const requestRef = useRef(0);

  const isMyDashboard = user?.id == userId;

  const groupedCountData = useSelector(
    (state) => state.DashboardReducer.groupedCountData,
  );
  const salesTrendData = useSelector(
    (state) => state.DashboardReducer.salesTrendData,
  );
  const groupedSourceData = useSelector(
    (state) => state.DashboardReducer.groupedSourceData,
  );
  const groupedIndustryData = useSelector(
    (state) => state.DashboardReducer.groupedIndustryData,
  );
  const convertedLeadsAllData = useSelector(
    (state) => state.DashboardReducer.convertedLeadsData,
  );
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );

  useEffect(() => {
    if (Object.keys(getUserInfo()).length) {
      const payload = { userId };
      const requestId = ++requestRef.current;
      setIsLoading(true);
      Promise.all([
        dispatch(getGroupedCountData(payload)),
        dispatch(getSalesTrendData(payload)),
        dispatch(getGroupedBySourceData(payload)),
        dispatch(getGroupedByIndustryData(payload)),
        dispatch(getLeadStatusList()),
        dispatch(getConvertedLeadsData(payload)),
      ]).finally(() => {
        if (requestId === requestRef.current) {
          setIsLoading(false);
        }
      });
    }
  }, [dispatch, userId]);

  let convertedLeadsData =
    convertedLeadsAllData?.length > 0 ? convertedLeadsAllData : [];

  let groupedIndustryData1 = [];
  let leadByIndustrySortedData = [];
  let groupedSourceData1 = [];
  let leadBySourceDataByOrder = [];
  if (groupedIndustryData?.dashboardDTOS?.length > 0) {
    groupedIndustryData?.dashboardDTOS?.forEach((record) => {
      if (record?.typeValue) {
        record.typeValue =
          record?.typeValue == "null" || record?.typeValue == ""
            ? "Others"
            : record?.typeValue;
        record.order = record?.typeValue == "Others" ? "b" : "a";
        record.countSum = Number(record.countSum);
        groupedIndustryData1.push(record);
      }
    });
    if (groupedIndustryData1) {
      leadByIndustrySortedData = _.orderBy(
        groupedIndustryData1,
        "order",
        "asc",
      );
    }
  }

  if (groupedSourceData?.dashboardDTOS?.length > 0) {
    groupedSourceData?.dashboardDTOS?.forEach((record) => {
      if (record?.typeValue) {
        record.typeValue =
          record?.typeValue == "null" || record?.typeValue == ""
            ? "Others"
            : record?.typeValue;
        record.order = record?.typeValue == "Others" ? "b" : "a";
        record.countSum = Number(record.countSum);
        groupedSourceData1.push(record);
      }
    });
    if (groupedSourceData1) {
      leadBySourceDataByOrder = _.orderBy(groupedSourceData1, "order", "asc");
    }
  }

  const statusMap = new Map();
  leadStatusListing.forEach((item) => {
    statusMap.set(item.id + "", item.text);
  });
  const monthNames = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  let salesTrendWonData = [];
  let salesTrendWonDataByMonth = [];
  if (salesTrendData && salesTrendData?.dashboardDTOS) {
    salesTrendData?.dashboardDTOS?.forEach((record) => {
      record.opportunitSum =
        record?.opportunitSum && record?.opportunitSum != "null"
          ? parseFloat(record?.opportunitSum)
          : 0;
      record.month = record?.typeValue ? monthNames[record.typeValue] : "";
      record.monthNum = record?.typeValue ? parseInt(record.typeValue) : 0;
      record.status = statusMap.get(record.statusId);
      salesTrendWonData.push(record);
    });
    if (salesTrendWonData) {
      salesTrendWonData = _.orderBy(salesTrendWonData, "status", "asc");
      salesTrendWonDataByMonth = _.map(
        _.groupBy(salesTrendWonData, "typeValue"),
        (o, month) => {
          return {
            month: month ? monthNames[month] : "",
            opportunitSum: _.sumBy(o, "opportunitSum"),
          };
        },
      );
    }
  }

  const handleRedirection = (path) => {
    if (!isMyDashboard) {
      showAlert(
        "info",
        "Redirection is not allowed when viewing another user's dashboard.",
      );
      return;
    }
    navigate(path);
  };

  return (
    <div className="tw-bg-[#f8fcff] tw-p-5">
      <div className="tw-mb-6">
        <Collapse
          expandIcon={({ isActive }) =>
            isActive ? <ChevronUp className="tw-w-4 tw-h-4" /> : <ChevronDown className="tw-w-4 tw-h-4" />
          }
          className="tw-advanced-filter"
          expandIconPosition="end"
          items={[
            {
              label: (
                <div className="tw-text-base tw-font-semibold">Marketing</div>
              ),
              children: (
                <Skeleton active loading={isLoading} paragraph={{ rows: 2 }}>
                  <Row className="tw-px-4 tw-py-2">
                  <Col md={3}>
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1"
                      onClick={() => handleRedirection(`/search-lead`)}
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <Filter className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        {groupedCountData?.leadGenareatedCount}
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        All New Leads
                      </div>
                    </div>
                  </Col>
                  <Col md={1}>
                    <img src={seprator} alt="" />
                  </Col>
                  <Col md={3}>
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=13`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <Send className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        0.0
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Campaign Sent
                      </div>
                    </div>
                  </Col>
                  <Col md={1}>
                    <img src={seprator} alt="" />
                  </Col>
                  <Col md={5}>
                    <div
                      className="tw-flex tw-flex-col tw-gap-1 tw-pb-1"
                      onClick={() =>
                        handleRedirection(`/search-lead?Lead Status=16`)
                      }
                    >
                      <div className="tw-flex tw-items-center tw-justify-center tw-bg-secondary-color tw-w-[34px] tw-h-[34px] tw-rounded-full">
                        <TrendingUp className="tw-w-4 tw-h-4 tw-text-text-primary" />
                      </div>
                      <div className="tw-text-3xl tw-font-semibold tw-mt-3">
                        0.0
                      </div>
                      <div className="tw-text-sm tw-font-semibold tw-text-sub-text">
                        Lead Generated Through Campaigns
                      </div>
                    </div>
                  </Col>
                  </Row>
                </Skeleton>
              ),
            },
          ]}
        />
      </div>

      <Row gutter={[24, 24]} className="mx-0 mb20">
        <Col xs={24} md={8} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Lead By Source"
            data={leadBySourceDataByOrder?.map((d) => ({
              label: d?.typeValue,
              value: d?.countSum || 0,
              sentiment:
                d?.countSum > 80 ? "High" : d?.countSum > 50 ? "Medium" : "Low",
              ...d,
            }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(
                      `/search-lead?leadSource=${clickedData.typeValue}`,
                    );
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} md={8}>
          <ChartRenderer
            loading={isLoading}
            title="Lead By Industry"
            data={leadByIndustrySortedData?.map((d) => ({
              label: d?.typeValue,
              value: d?.countSum || 0,
              sentiment:
                d?.countSum > 80 ? "High" : d?.countSum > 50 ? "Medium" : "Low",
              ...d,
            }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              yAxis: {
                label: {
                  formatter: (text) =>
                    text.length > 10 ? `${text.slice(0, 10)}...` : text,
                },
              },
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(
                      clickedData.typeValueId !== "null"
                        ? `/search-lead?Industry=${clickedData.typeValueId}`
                        : "/search-lead",
                    );
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} md={8} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Converted Leads"
            data={convertedLeadsData?.map((d) => ({
              value: parseFloat(d?.oppurtunityAmount) || 0,
              label: d?.fullname,
            }))}
          />
        </Col>
      </Row>

      <Row gutter={[24, 24]} className="mx-0 mb20">
        <Col xs={24} md={16} className="!tw-pl-0">
          <ChartRenderer
            loading={isLoading}
            title="Lead By Status"
            type="column"
            data={salesTrendWonData?.map((d) => ({
              label: d?.status,
              value: d?.opportunitSum || 0,
              sentiment:
                d?.opportunitSum > 200000
                  ? "High"
                  : d?.opportunitSum > 100000
                    ? "Medium"
                    : "Low",
              ...d,
            }))}
            customConfig={{
              seriesField: "sentiment",
              color: getSentimentColor,
              onReady: (plot) => {
                plot.on("element:click", (e) => {
                  const clickedData = e.data?.data;
                  if (clickedData) {
                    handleRedirection(
                      `/search-lead?Industry=${clickedData.typeValue}`,
                    );
                  }
                });
              },
            }}
          />
        </Col>
        <Col xs={24} md={8} className="!tw-pr-0">
          <ChartRenderer
            loading={isLoading}
            title="Lead Trend"
            data={salesTrendWonDataByMonth?.map((d) => ({
              label: d?.month,
              value: d?.opportunitSum || 0,
            }))}
          />
        </Col>
      </Row>
    </div>
  );
};

export default MarketingReport;
